import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      nav: { home: 'Home', pricing: 'Pricing', login: 'Log In', signup: 'Sign Up', dashboard: 'Dashboard', logout: 'Log Out' },
      hero: {
        badge: 'Production-Ready on Hedera Mainnet',
        title1: 'The Intelligent Notary',
        title2: 'Platform with',
        title3: 'Unbreakable Trust',
        subtitle: 'NotaryChain fuses AI document intelligence, biometric verification, and blockchain-sealed integrity into one enterprise-grade platform.',
        cta_start: 'Get Started Free',
        cta_demo: 'Live Demo',
      },
      trust: { soc2: 'SOC2 Compliant', hedera: 'Hedera Mainnet', ai: 'AI-Powered', biometric: 'Biometric ID' },
      auth: {
        sign_in: 'Sign In',
        sign_up: 'Sign Up',
        email: 'Email Address',
        password: 'Password',
        confirm_password: 'Confirm Password',
        full_name: 'Full Name',
        forgot: 'Forgot password?',
        no_account: "Don't have an account?",
        has_account: 'Already have an account?',
        or_continue: 'Or continue with',
        welcome_back: 'Welcome Back',
        create_account: 'Create Your Account',
      },
      dashboard: {
        title: 'Dashboard',
        welcome: 'Welcome back',
        documents: 'Documents',
        pending: 'Pending',
        completed: 'Completed',
        recent: 'Recent Activity',
      },
      marketplace: {
        title: 'Notary Marketplace',
        subtitle: 'Find and connect with certified notaries',
        back: 'Back to Marketplace',
        search_placeholder: 'Search by name, state, or bio...',
        all_states: 'All States',
        all_specs: 'All Specializations',
        top_rated: 'Top Rated',
        most_experienced: 'Most Experienced',
        lowest_rate: 'Lowest Rate',
        no_results: 'No notaries found matching your criteria.',
        reviews: 'Reviews',
        write_review: 'Write Review',
        submit_review: 'Submit Review',
        rate_experience: 'Rate your experience',
        share_experience: 'Share your experience...',
        no_reviews: 'No reviews yet.',
        book_session: 'Book a Session',
        availability: 'Availability This Week',
        no_availability: 'No availability data.',
        rate: 'Rate',
        experience: 'Experience',
        done: 'done',
        ron_certified: 'RON Certified',
        closed: 'Closed',
        available: 'Available',
      },
      notary: {
        workstation: 'Assurance Portal',
        user_view: 'User View',
        todays_earnings: "Today's Earnings",
        total_completed: 'Total Completed',
        in_progress: 'In Progress',
        available_label: 'Available',
        lifetime_earnings: 'Lifetime Earnings',
        active_session: 'Active Session in Progress',
        sessions_running: 'session(s) running',
        rejoin: 'Rejoin Session',
        tab_available: 'Available',
        tab_my_requests: 'My Requests',
        tab_history: 'History',
        tab_schedule: 'Schedule',
        search_requests: 'Search requests...',
        no_available: 'No available requests',
        new_requests_appear: 'New notarization requests will appear here',
        no_assigned: 'No active requests',
        no_completed: 'No completed notarizations yet',
      },
      security: {
        title: 'Security Settings',
        two_factor: 'Two-Factor Authentication',
        enabled: 'Enabled',
        not_enabled: 'Not Enabled',
        enabled_desc: 'Your account is protected with an authenticator app.',
        disabled_desc: 'Add an extra layer of security to your account by enabling 2FA.',
        enable_2fa: 'Enable 2FA',
        disable_2fa: 'Disable 2FA',
        setup_title: 'Set Up Two-Factor Authentication',
        setup_desc: 'Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)',
        manual_key: "Can't scan? Enter this key manually:",
        backup_codes: 'Backup Codes',
        backup_desc: 'Save these codes in a safe place. Each can be used once if you lose access to your authenticator.',
        copy_all: 'Copy All',
        verify_setup: 'Verify Setup',
        verify_desc: 'Enter the 6-digit code from your authenticator app to confirm setup.',
        verify_enable: 'Verify & Enable',
        regenerate: 'Regenerate Backup Codes',
        new_backup: 'New Backup Codes',
        new_backup_desc: 'Save these codes. Your previous backup codes are no longer valid.',
        dismiss: 'Dismiss',
        disable_title: 'Disable Two-Factor Authentication',
        disable_desc: 'This will remove the extra security layer from your account. Enter your password and a current 2FA code to confirm.',
        twofa_code: '2FA Code',
        codes_remaining: 'backup codes remaining',
      },
      common: { loading: 'Loading...', save: 'Save', cancel: 'Cancel', delete: 'Delete', edit: 'Edit', search: 'Search', back: 'Back' },
    },
  },
  es: {
    translation: {
      nav: { home: 'Inicio', pricing: 'Precios', login: 'Iniciar Sesion', signup: 'Registrarse', dashboard: 'Panel', logout: 'Cerrar Sesion' },
      hero: {
        badge: 'Produccion lista en Hedera Mainnet',
        title1: 'La Plataforma Notarial',
        title2: 'Inteligente con',
        title3: 'Confianza Inquebrantable',
        subtitle: 'NotaryChain fusiona inteligencia documental IA, verificacion biometrica e integridad sellada en blockchain en una plataforma empresarial.',
        cta_start: 'Comenzar Gratis',
        cta_demo: 'Demo en Vivo',
      },
      trust: { soc2: 'Cumplimiento SOC2', hedera: 'Hedera Mainnet', ai: 'Potenciado por IA', biometric: 'ID Biometrico' },
      auth: {
        sign_in: 'Iniciar Sesion',
        sign_up: 'Registrarse',
        email: 'Correo Electronico',
        password: 'Contrasena',
        confirm_password: 'Confirmar Contrasena',
        full_name: 'Nombre Completo',
        forgot: 'Olvidaste la contrasena?',
        no_account: 'No tienes una cuenta?',
        has_account: 'Ya tienes una cuenta?',
        or_continue: 'O continuar con',
        welcome_back: 'Bienvenido de Nuevo',
        create_account: 'Crea Tu Cuenta',
      },
      dashboard: {
        title: 'Panel',
        welcome: 'Bienvenido de nuevo',
        documents: 'Documentos',
        pending: 'Pendientes',
        completed: 'Completados',
        recent: 'Actividad Reciente',
      },
      marketplace: {
        title: 'Mercado de Notarios',
        subtitle: 'Encuentra y conecta con notarios certificados',
        back: 'Volver al Mercado',
        search_placeholder: 'Buscar por nombre, estado o perfil...',
        all_states: 'Todos los Estados',
        all_specs: 'Todas las Especialidades',
        top_rated: 'Mejor Valorados',
        most_experienced: 'Mas Experimentados',
        lowest_rate: 'Tarifa mas Baja',
        no_results: 'No se encontraron notarios con sus criterios.',
        reviews: 'Resenas',
        write_review: 'Escribir Resena',
        submit_review: 'Enviar Resena',
        rate_experience: 'Califica tu experiencia',
        share_experience: 'Comparte tu experiencia...',
        no_reviews: 'Sin resenas aun.',
        book_session: 'Reservar una Sesion',
        availability: 'Disponibilidad esta Semana',
        no_availability: 'Sin datos de disponibilidad.',
        rate: 'Tarifa',
        experience: 'Experiencia',
        done: 'realizados',
        ron_certified: 'Certificado RON',
        closed: 'Cerrado',
        available: 'Disponible',
      },
      notary: {
        workstation: 'Estacion de Trabajo Notarial',
        user_view: 'Vista de Usuario',
        todays_earnings: 'Ganancias de Hoy',
        total_completed: 'Total Completados',
        in_progress: 'En Progreso',
        available_label: 'Disponibles',
        lifetime_earnings: 'Ganancias Totales',
        active_session: 'Sesion Activa en Progreso',
        sessions_running: 'sesion(es) en curso',
        rejoin: 'Reunirse a la Sesion',
        tab_available: 'Disponibles',
        tab_my_requests: 'Mis Solicitudes',
        tab_history: 'Historial',
        tab_schedule: 'Horario',
        search_requests: 'Buscar solicitudes...',
        no_available: 'No hay solicitudes disponibles',
        new_requests_appear: 'Las nuevas solicitudes de notarizacion apareceran aqui',
        no_assigned: 'Sin solicitudes activas',
        no_completed: 'Sin notarizaciones completadas aun',
      },
      security: {
        title: 'Configuracion de Seguridad',
        two_factor: 'Autenticacion de Dos Factores',
        enabled: 'Activado',
        not_enabled: 'No Activado',
        enabled_desc: 'Tu cuenta esta protegida con una app de autenticacion.',
        disabled_desc: 'Agrega una capa extra de seguridad a tu cuenta habilitando 2FA.',
        enable_2fa: 'Activar 2FA',
        disable_2fa: 'Desactivar 2FA',
        setup_title: 'Configurar Autenticacion de Dos Factores',
        setup_desc: 'Escanea este codigo QR con tu app de autenticacion (Google Authenticator, Authy, etc.)',
        manual_key: 'No puedes escanear? Ingresa esta clave manualmente:',
        backup_codes: 'Codigos de Respaldo',
        backup_desc: 'Guarda estos codigos en un lugar seguro. Cada uno se puede usar una vez si pierdes acceso a tu autenticador.',
        copy_all: 'Copiar Todo',
        verify_setup: 'Verificar Configuracion',
        verify_desc: 'Ingresa el codigo de 6 digitos de tu app de autenticacion para confirmar.',
        verify_enable: 'Verificar y Activar',
        regenerate: 'Regenerar Codigos de Respaldo',
        new_backup: 'Nuevos Codigos de Respaldo',
        new_backup_desc: 'Guarda estos codigos. Tus codigos anteriores ya no son validos.',
        dismiss: 'Cerrar',
        disable_title: 'Desactivar Autenticacion de Dos Factores',
        disable_desc: 'Esto eliminara la capa de seguridad adicional de tu cuenta. Ingresa tu contrasena y un codigo 2FA actual para confirmar.',
        twofa_code: 'Codigo 2FA',
        codes_remaining: 'codigos de respaldo restantes',
      },
      common: { loading: 'Cargando...', save: 'Guardar', cancel: 'Cancelar', delete: 'Eliminar', edit: 'Editar', search: 'Buscar', back: 'Volver' },
    },
  },
  fr: {
    translation: {
      nav: { home: 'Accueil', pricing: 'Tarifs', login: 'Connexion', signup: 'Inscription', dashboard: 'Tableau de bord', logout: 'Deconnexion' },
      hero: {
        badge: 'Pret pour la production sur Hedera Mainnet',
        title1: 'La Plateforme Notariale',
        title2: 'Intelligente avec une',
        title3: 'Confiance Indestructible',
        subtitle: "NotaryChain fusionne l'intelligence documentaire IA, la verification biometrique et l'integrite scellee par blockchain en une plateforme professionnelle.",
        cta_start: 'Commencer Gratuitement',
        cta_demo: 'Demo en Direct',
      },
      trust: { soc2: 'Conforme SOC2', hedera: 'Hedera Mainnet', ai: 'Propulse par IA', biometric: 'ID Biometrique' },
      auth: {
        sign_in: 'Connexion',
        sign_up: 'Inscription',
        email: 'Adresse Email',
        password: 'Mot de Passe',
        confirm_password: 'Confirmer le Mot de Passe',
        full_name: 'Nom Complet',
        forgot: 'Mot de passe oublie?',
        no_account: "Pas encore de compte?",
        has_account: 'Deja un compte?',
        or_continue: 'Ou continuer avec',
        welcome_back: 'Bon Retour',
        create_account: 'Creez Votre Compte',
      },
      dashboard: {
        title: 'Tableau de bord',
        welcome: 'Bon retour',
        documents: 'Documents',
        pending: 'En attente',
        completed: 'Termines',
        recent: 'Activite Recente',
      },
      marketplace: {
        title: 'Place de Marche Notariale',
        subtitle: 'Trouvez et connectez-vous avec des notaires certifies',
        back: 'Retour au Marche',
        search_placeholder: 'Rechercher par nom, etat ou profil...',
        all_states: 'Tous les Etats',
        all_specs: 'Toutes les Specialisations',
        top_rated: 'Mieux Notes',
        most_experienced: 'Plus Experimentes',
        lowest_rate: 'Tarif le Plus Bas',
        no_results: 'Aucun notaire trouve correspondant a vos criteres.',
        reviews: 'Avis',
        write_review: 'Ecrire un Avis',
        submit_review: 'Soumettre un Avis',
        rate_experience: 'Evaluez votre experience',
        share_experience: 'Partagez votre experience...',
        no_reviews: 'Pas encore d\'avis.',
        book_session: 'Reserver une Session',
        availability: 'Disponibilite cette Semaine',
        no_availability: 'Aucune donnee de disponibilite.',
        rate: 'Tarif',
        experience: 'Experience',
        done: 'effectues',
        ron_certified: 'Certifie RON',
        closed: 'Ferme',
        available: 'Disponible',
      },
      notary: {
        workstation: 'Poste de Travail Notarial',
        user_view: 'Vue Utilisateur',
        todays_earnings: "Gains d'Aujourd'hui",
        total_completed: 'Total Termines',
        in_progress: 'En Cours',
        available_label: 'Disponibles',
        lifetime_earnings: 'Gains Totaux',
        active_session: 'Session Active en Cours',
        sessions_running: 'session(s) en cours',
        rejoin: 'Rejoindre la Session',
        tab_available: 'Disponibles',
        tab_my_requests: 'Mes Demandes',
        tab_history: 'Historique',
        tab_schedule: 'Planning',
        search_requests: 'Rechercher des demandes...',
        no_available: 'Aucune demande disponible',
        new_requests_appear: 'Les nouvelles demandes de notarisation apparaitront ici',
        no_assigned: 'Aucune demande active',
        no_completed: 'Aucune notarisation terminee pour le moment',
      },
      security: {
        title: 'Parametres de Securite',
        two_factor: 'Authentification a Deux Facteurs',
        enabled: 'Active',
        not_enabled: 'Non Active',
        enabled_desc: 'Votre compte est protege par une application d\'authentification.',
        disabled_desc: 'Ajoutez une couche de securite supplementaire a votre compte en activant le 2FA.',
        enable_2fa: 'Activer 2FA',
        disable_2fa: 'Desactiver 2FA',
        setup_title: 'Configurer l\'Authentification a Deux Facteurs',
        setup_desc: 'Scannez ce code QR avec votre application d\'authentification (Google Authenticator, Authy, etc.)',
        manual_key: 'Impossible de scanner? Entrez cette cle manuellement:',
        backup_codes: 'Codes de Secours',
        backup_desc: 'Sauvegardez ces codes dans un endroit sur. Chacun peut etre utilise une fois si vous perdez l\'acces a votre authentificateur.',
        copy_all: 'Copier Tout',
        verify_setup: 'Verifier la Configuration',
        verify_desc: 'Entrez le code a 6 chiffres de votre application d\'authentification pour confirmer.',
        verify_enable: 'Verifier et Activer',
        regenerate: 'Regenerer les Codes de Secours',
        new_backup: 'Nouveaux Codes de Secours',
        new_backup_desc: 'Sauvegardez ces codes. Vos anciens codes ne sont plus valides.',
        dismiss: 'Fermer',
        disable_title: 'Desactiver l\'Authentification a Deux Facteurs',
        disable_desc: 'Cela supprimera la couche de securite supplementaire de votre compte. Entrez votre mot de passe et un code 2FA actuel pour confirmer.',
        twofa_code: 'Code 2FA',
        codes_remaining: 'codes de secours restants',
      },
      common: { loading: 'Chargement...', save: 'Enregistrer', cancel: 'Annuler', delete: 'Supprimer', edit: 'Modifier', search: 'Rechercher', back: 'Retour' },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

export default i18n;
